import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import Table from '../../../../components/Table';
import Pagination from '../../../../components/Pagination';
import SearchableClientDropdown from '../../../../components/SearchableClientDropdown';
import TableSkeleton from '../../../../components/TableSkeleton';
import { PlusIcon } from '../../../../components/IconButton/icons';
import ListPage, { FilterTrigger, SearchInput } from '../../../../components/ListPage';
import { useServerFiltering } from '../../../../utils/useServerFiltering';
import { useUrlFilters } from '../../../../utils/useUrlFilters';
import ContactColumn from '../ContactColumn';
import AddContactModal from '../AddContactModal';
import ClientDetailModal from '../ClientDetailModal';
import {
  Contact,
  contactColumns,
  DEFAULT_PAGE_SIZE,
  SEARCH_DEBOUNCE_DELAY
} from '../../config';

const CONTACT_FILTER_DEFINITIONS = {
  search: { param: 'q', defaultValue: '', type: 'string' as const },
  client: { param: 'client', defaultValue: '', type: 'string' as const },
  page: { param: 'page', defaultValue: 1, type: 'number' as const },
  pageSize: { param: 'size', defaultValue: 25, type: 'number' as const },
};

interface ContactsViewProps {
  viewToggle: React.ReactNode;
}

const ContactsView: React.FC<ContactsViewProps> = ({ viewToggle }) => {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState<number | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  const {
    filters: urlFilters,
    setFilter,
    setFilters: setUrlFilters,
    resetFilters: resetUrlFilters,
    hasActiveFilters
  } = useUrlFilters(CONTACT_FILTER_DEFINITIONS);

  const searchInput = urlFilters.search;
  const filterClient = useMemo(() =>
    urlFilters.client ? [urlFilters.client] : [],
    [urlFilters.client]
  );
  const urlPage = urlFilters.page;
  const urlPageSize = urlFilters.pageSize;

  const {
    data: contacts,
    pagination,
    loading,
    filtering,
    error,
    setFilters,
    refetch
  } = useServerFiltering<Contact>({
    rpcFunction: 'get_filtered_contacts',
    defaultFilters: {
      search_term: null,
      client_filter: null,
      page_size: DEFAULT_PAGE_SIZE,
      page_offset: 0,
      sort_by: 'created_at',
      sort_order: 'desc'
    },
    debounceDelay: SEARCH_DEBOUNCE_DELAY
  });

  useEffect(() => {
    const pageSize = urlPageSize || DEFAULT_PAGE_SIZE;
    const pageOffset = ((urlPage || 1) - 1) * pageSize;

    setFilters({
      search_term: searchInput || null,
      client_filter: filterClient.length > 0 ? filterClient : null,
      page_size: pageSize,
      page_offset: pageOffset
    });
  }, [searchInput, filterClient, urlPage, urlPageSize, setFilters]);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setUrlFilters({ search: e.target.value, page: 1 });
  }, [setUrlFilters]);

  const handleClientChange = useCallback((clientId: string | number | null) => {
    if (clientId === null || clientId === 'none') {
      setUrlFilters({ client: '', page: 1 });
    } else {
      setUrlFilters({ client: clientId.toString(), page: 1 });
    }
  }, [setUrlFilters]);

  const handleClearFilters = useCallback(() => {
    resetUrlFilters();
  }, [resetUrlFilters]);

  const handleClearSearch = useCallback(() => {
    setUrlFilters({ search: '', page: 1 });
  }, [setUrlFilters]);

  const handlePageChange = useCallback((page: number) => {
    setFilter('page', page);
  }, [setFilter]);

  const handlePageSizeChange = useCallback((newPageSize: number) => {
    setUrlFilters({ pageSize: newPageSize, page: 1 });
  }, [setUrlFilters]);

  const handleContactCreated = useCallback(() => {
    refetch();
  }, [refetch]);

  const handleRowClick = useCallback((row: Contact) => {
    setSelectedClientId(row.client.id);
    setIsDetailModalOpen(true);
  }, []);

  const handleDetailModalClose = useCallback(() => {
    setIsDetailModalOpen(false);
    setSelectedClientId(null);
  }, []);

  const handleClientUpdated = useCallback(() => {
    refetch();
  }, [refetch]);

  const columns = useMemo<ColumnDef<Contact>[]>(
    () => {
      // Column budget (table-layout: fixed; see the Documents register).
      // Name is the one `auto` column; Client and Email take a share each.
      const columnConfig: Record<string, { minWidth?: string; width?: string; maxWidth?: string }> = {
        name: { minWidth: '180px', width: 'auto' },
        client: { minWidth: '160px', width: '24%' },
        email: { minWidth: '180px', width: '26%' },
        phone: { minWidth: '140px', width: '140px' },
        created_at: { minWidth: '112px', width: '112px' }
      };

      return Object.entries(contactColumns)
        .sort(([, a], [, b]) => (a.order || 999) - (b.order || 999))
        .map(([key, config]) => ({
          header: config.label || key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' '),
          accessorKey: key,
          meta: columnConfig[key],
          cell: ({ row }: { row: any }) => (
            <ContactColumn
              column={key}
              config={config}
              row={row}
            />
          )
        }));
    },
    []
  );

  const currentPage = pagination?.page || urlPage || 1;
  const currentPageSize = pagination?.page_size || urlPageSize || DEFAULT_PAGE_SIZE;

  const initialLoading = loading && !contacts.length;

  return (
    <ListPage
      title="Contacts"
      className="contacts-page"
      loading={initialLoading}
      skeleton={{ segmentCount: 2, filterCount: 1, showAction: true }}
      search={
        <SearchInput
          placeholder="Search by name, email or client…"
          value={searchInput}
          onChange={handleSearchChange}
          onClear={handleClearSearch}
          aria-label="Search contacts"
        />
      }
      actions={
        <button type="button" className="pill-btn pill-btn--solid pill-btn--sm" onClick={() => setIsAddModalOpen(true)} disabled={loading}>
          <PlusIcon /> Add contact
        </button>
      }
      segments={viewToggle}
      filters={
        <FilterTrigger label="Client" active={filterClient.length > 0}>
          <SearchableClientDropdown
            value={filterClient.length > 0 ? filterClient[0] : null}
            onChange={(clientId) => handleClientChange(clientId)}
            placeholder="All"
            allowNone={true}
            noneLabel="All Clients"
          />
        </FilterTrigger>
      }
      hasActiveFilters={hasActiveFilters}
      onClearFilters={handleClearFilters}
      resultsText={
        !loading && pagination && (
          <>
            Showing {pagination.total_records > 0 ? ((currentPage - 1) * currentPageSize) + 1 : 0}–{Math.min(currentPage * currentPageSize, pagination.total_records)} of {pagination.total_records.toLocaleString()} contacts
          </>
        )
      }
      footer={
        <Pagination
          variant="bar"
          currentPage={currentPage}
          totalPages={pagination?.total_pages ?? 1}
          totalRecords={pagination?.total_records ?? 0}
          pageSize={currentPageSize}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          hasNext={pagination?.has_next ?? false}
          hasPrevious={pagination?.has_previous ?? false}
          loading={loading || filtering}
          showPageInfo
          pageSizeOptions={[25, 50, 100]}
        />
      }
    >
      {error && (
        <div className="list-page__error">
          Error loading contacts: {error}
        </div>
      )}

      {initialLoading ? (
        <TableSkeleton compact rows={14} columns={5} showHeader />
      ) : contacts.length === 0 ? (
        <div className="list-page__empty">
          {hasActiveFilters ? 'No contacts match these filters.' : 'No contacts yet. Add a contact to get started.'}
        </div>
      ) : (
        <div className={`list-page__body ${filtering ? 'is-filtering' : ''}`.trim()}>
          <Table data={contacts} columns={columns} onRowClick={handleRowClick} stickyHeader />
        </div>
      )}

      <AddContactModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onContactCreated={handleContactCreated}
      />

      <ClientDetailModal
        isOpen={isDetailModalOpen}
        clientId={selectedClientId}
        onClose={handleDetailModalClose}
        onClientUpdated={handleClientUpdated}
        initialTab="contacts"
      />
    </ListPage>
  );
};

export default ContactsView;
